import { keepPreviousData, useMutation, useQuery } from '@tanstack/react-query'
import { apiClient } from '../../api'
import { AgentFilter } from '../../types/analyticsTypes'

const BI_STALE_TIME = 5 * 60 * 1000

export const useAnalyticsAgents = () => {
  return useQuery({
    queryKey: ['analytics', 'agents'],
    queryFn: apiClient.analytics.fetchAgents,
    staleTime: BI_STALE_TIME,
  })
}

/** Display name of the selected agent, or null for the all-agents scope. */
export const useAgentName = (agentId: AgentFilter): string | null => {
  const { data: agents = [] } = useAnalyticsAgents()
  return agents.find(agent => agent.id === agentId)?.name ?? null
}

/** Suffix for widget titles so an agent-scoped view is unmistakable. */
export const agentScopeSuffix = (agentName: string | null): string =>
  agentName ? ` (Agent: ${agentName})` : ''

export const useFeedbackSummary = (agentId: AgentFilter, days: number | null) => {
  return useQuery({
    queryKey: ['analytics', 'feedback-summary', agentId, days],
    queryFn: () => apiClient.analytics.fetchFeedbackSummary(agentId, days),
    placeholderData: keepPreviousData,
    staleTime: BI_STALE_TIME,
  })
}

export const useWeeklyActiveUsers = (agentId: AgentFilter) => {
  return useQuery({
    queryKey: ['analytics', 'weekly-active-users', agentId],
    queryFn: () => apiClient.analytics.fetchWeeklyActiveUsers(agentId),
    placeholderData: keepPreviousData,
    staleTime: BI_STALE_TIME,
  })
}

export const useConversationsPerDay = (agentId: AgentFilter) => {
  return useQuery({
    queryKey: ['analytics', 'conversations-per-day', agentId],
    queryFn: () => apiClient.analytics.fetchConversationsPerDay(agentId),
    placeholderData: keepPreviousData,
    staleTime: BI_STALE_TIME,
  })
}

export const useConversationsPerWeek = (agentId: AgentFilter) => {
  return useQuery({
    queryKey: ['analytics', 'conversations-per-week', agentId],
    queryFn: () => apiClient.analytics.fetchConversationsPerWeek(agentId),
    placeholderData: keepPreviousData,
    staleTime: BI_STALE_TIME,
  })
}

export const useMessagesPerDay = (agentId: AgentFilter) => {
  return useQuery({
    queryKey: ['analytics', 'messages-per-day', agentId],
    queryFn: () => apiClient.analytics.fetchMessagesPerDay(agentId),
    placeholderData: keepPreviousData,
    staleTime: BI_STALE_TIME,
  })
}

export const useMessagesPerWeek = (agentId: AgentFilter) => {
  return useQuery({
    queryKey: ['analytics', 'messages-per-week', agentId],
    queryFn: () => apiClient.analytics.fetchMessagesPerWeek(agentId),
    placeholderData: keepPreviousData,
    staleTime: BI_STALE_TIME,
  })
}

export const useUsersPerDay = (agentId: AgentFilter) => {
  return useQuery({
    queryKey: ['analytics', 'users-per-day', agentId],
    queryFn: () => apiClient.analytics.fetchUsersPerDay(agentId),
    placeholderData: keepPreviousData,
    staleTime: BI_STALE_TIME,
  })
}

export const useAnswersPerAgent = (agentId: AgentFilter) => {
  return useQuery({
    queryKey: ['analytics', 'answers-per-agent', agentId],
    queryFn: () => apiClient.analytics.fetchAnswersPerAgent(agentId),
    placeholderData: keepPreviousData,
    staleTime: BI_STALE_TIME,
  })
}

export const useNegativeFeedback = (
  limit: number,
  offset: number,
  days: number,
  agentId: AgentFilter,
) => {
  return useQuery({
    queryKey: ['analytics', 'negative-feedback', limit, offset, days, agentId],
    queryFn: () => apiClient.analytics.fetchNegativeFeedback(limit, offset, days, agentId),
    placeholderData: keepPreviousData,
    staleTime: BI_STALE_TIME,
  })
}

export const useNegativeFeedbackConversation = (messageId: string | null) => {
  return useQuery({
    queryKey: ['analytics', 'negative-feedback', messageId, 'conversation'],
    queryFn: () => apiClient.analytics.fetchNegativeFeedbackConversation(messageId as string),
    enabled: messageId !== null,
    staleTime: BI_STALE_TIME,
  })
}

/** Fetches the filtered negative-feedback workbook and hands it to the browser
 * as a file download. The endpoint is Bearer-authed, so a plain link cannot
 * be used; the blob is turned into a short-lived object URL instead. */
export const useExportNegativeFeedback = () => {
  return useMutation({
    mutationFn: async ({
      days,
      agentId,
      filename,
    }: {
      days: number
      agentId: AgentFilter
      filename: string
    }) => {
      const blob = await apiClient.analytics.exportNegativeFeedback(days, agentId)
      const url = URL.createObjectURL(blob)
      const anchor = document.createElement('a')
      anchor.href = url
      anchor.download = filename
      document.body.appendChild(anchor)
      anchor.click()
      anchor.remove()
      URL.revokeObjectURL(url)
    },
  })
}
